import { counterSource } from './sample';

export type ExampleCategory = 'Logic gates' | 'Selection logic' | 'Sequential' | 'Arithmetic & systems';

export interface ExampleDefinition {
  label: string;
  filename: string;
  source: string;
  category: ExampleCategory;
  summary: string;
  hardware: string;
  family?: 'mux-style' | 'priority-style';
  style?: string;
  takeaway?: string;
  topModule?: string;
}

export const examples: Record<string, ExampleDefinition> = {
  andGate: {
    label: 'AND gate', filename: 'and_gate.sv', category: 'Logic gates', hardware: '1 × AND gate',
    summary: 'Two inputs must both be high.', takeaway: 'A bitwise & maps directly to an AND primitive.',
    source: `module and_gate(input logic a, input logic b, output logic y);
    assign y = a & b;
endmodule`,
  },
  orGate: {
    label: 'OR gate', filename: 'or_gate.sv', category: 'Logic gates', hardware: '1 × OR gate',
    summary: 'Either high input drives the output.', takeaway: 'A bitwise | maps directly to an OR primitive.',
    source: `module or_gate(input logic a, input logic b, output logic y);
    assign y = a | b;
endmodule`,
  },
  xorGate: {
    label: 'XOR gate', filename: 'xor_gate.sv', category: 'Logic gates', hardware: '1 × XOR gate',
    summary: 'Output is high when inputs differ.', takeaway: 'XOR is common in parity, adders, and checksums.',
    source: `module xor_gate(input logic a, input logic b, output logic y);
    assign y = a ^ b;
endmodule`,
  },
  inverter: {
    label: 'NOT gate', filename: 'not_gate.sv', category: 'Logic gates', hardware: '1 × inverter',
    summary: 'Inverts a single logic signal.', takeaway: 'The output bubble denotes logical inversion.',
    source: `module not_gate(input logic a, output logic y);
    assign y = ~a;
endmodule`,
  },
  muxTernary: {
    label: '2:1 mux — ternary', filename: 'mux_ternary.sv', category: 'Selection logic', family: 'mux-style', style: 'assign ? :', hardware: '1 × 2:1 mux',
    summary: 'Compact expression for two-way selection.', takeaway: 'Ternary, if/else, and a complete 2-item case infer the same 2:1 selection hardware.',
    source: `module mux_ternary(
    input  logic [7:0] a, b,
    input  logic       sel,
    output logic [7:0] y
);
    assign y = sel ? a : b;
endmodule`,
  },
  muxIfElse: {
    label: '2:1 mux — if/else', filename: 'mux_if_else.sv', category: 'Selection logic', family: 'mux-style', style: 'if / else', hardware: '1 × 2:1 mux',
    summary: 'Procedural two-way selection.', takeaway: 'A complete combinational if/else does not create storage; it infers a mux.',
    source: `module mux_if_else(
    input  logic [7:0] a, b,
    input  logic       sel,
    output logic [7:0] y
);
    always_comb begin
        if (sel) y = a;
        else     y = b;
    end
endmodule`,
  },
  muxCase: {
    label: '2:1 mux — case', filename: 'mux_case.sv', category: 'Selection logic', family: 'mux-style', style: 'case', hardware: '1 × 2:1 mux',
    summary: 'Truth-table style two-way selection.', takeaway: 'A complete case expresses the same 2:1 mux with explicit selector values.',
    source: `module mux_case(
    input  logic [7:0] a, b,
    input  logic       sel,
    output logic [7:0] y
);
    always_comb begin
        case (sel)
            1'b0: y = b;
            1'b1: y = a;
        endcase
    end
endmodule`,
  },
  priorityIf: {
    label: '4-way select — if chain', filename: 'priority_if.sv', category: 'Selection logic', family: 'priority-style', style: 'else-if chain', hardware: '3 × cascaded 2:1 muxes',
    summary: 'Each earlier condition has priority.', takeaway: 'An else-if chain is visualized as a decision cascade, exposing its priority depth.',
    source: `module priority_if(
    input  logic [7:0] d0, d1, d2, d3,
    input  logic [1:0] sel,
    output logic [7:0] y
);
    always_comb begin
        if      (sel == 2'b00) y = d0;
        else if (sel == 2'b01) y = d1;
        else if (sel == 2'b10) y = d2;
        else                   y = d3;
    end
endmodule`,
  },
  parallelCase: {
    label: '4-way select — case', filename: 'parallel_case.sv', category: 'Selection logic', family: 'priority-style', style: 'case table', hardware: '1 × 4:1 mux',
    summary: 'All selector values are listed in one table.', takeaway: 'The case form presents a single multi-input selector instead of a visible decision chain.',
    source: `module parallel_case(
    input  logic [7:0] d0, d1, d2, d3,
    input  logic [1:0] sel,
    output logic [7:0] y
);
    always_comb begin
        case (sel)
            2'b00: y = d0;
            2'b01: y = d1;
            2'b10: y = d2;
            default: y = d3;
        endcase
    end
endmodule`,
  },
  decoder: {
    label: '2-to-4 decoder', filename: 'decoder.sv', category: 'Selection logic', hardware: 'Decode network',
    summary: 'One-hot output selected by a binary input.',
    source: `module decoder(input logic [1:0] a, input logic en, output logic [3:0] y);
always_comb begin
    y = 4'b0000;
    if (en) y[a] = 1'b1;
end
endmodule`,
  },
  dff: {
    label: 'D flip-flop', filename: 'dff.sv', category: 'Sequential', hardware: '1 × D register',
    summary: 'Single-bit storage with asynchronous reset.',
    source: `module dff(input logic clk, rst_n, d, output logic q);
always_ff @(posedge clk or negedge rst_n) begin
    if (!rst_n) q <= 1'b0;
    else q <= d;
end
endmodule`,
  },
  counter: { label: '8-bit counter', filename: 'counter.sv', source: counterSource, category: 'Sequential', hardware: 'Register + incrementer', summary: 'Enabled state with feedback through an adder.' },
  shift: {
    label: 'Shift register', filename: 'shift_register.sv', category: 'Sequential', hardware: '8-bit register + shift path',
    summary: 'Moves data one position per enabled clock.',
    source: `module shift_register(input logic clk, rst_n, en, serial_in, output logic [7:0] q);
always_ff @(posedge clk) begin
    if (!rst_n) q <= '0;
    else if (en) q <= {q[6:0], serial_in};
end
endmodule`,
  },
  fsm: {
    label: 'Simple FSM', filename: 'simple_fsm.sv', category: 'Sequential', hardware: 'State register + next-state logic',
    summary: 'Three-state controller with explicit transitions.',
    source: `module simple_fsm(input logic clk, rst_n, start, done, output logic busy);
typedef enum logic [1:0] { IDLE, WORK, FINISH } state_t;
state_t state, next_state;
always_ff @(posedge clk) begin
    if (!rst_n) state <= IDLE;
    else state <= next_state;
end
always_comb begin
    next_state = state;
    case (state)
        IDLE: if (start) next_state = WORK;
        WORK: if (done) next_state = FINISH;
        FINISH: next_state = IDLE;
    endcase
end
assign busy = (state == WORK);
endmodule`,
  },
  adder: {
    label: '8-bit adder', filename: 'adder.sv', category: 'Arithmetic & systems', hardware: '1 × 8-bit adder',
    summary: 'Combinational arithmetic data path.',
    source: `module adder(input logic [7:0] a, b, output logic [7:0] sum);
    assign sum = a + b;
endmodule`,
  },
  alu: {
    label: '8-bit ALU', filename: 'alu.sv', category: 'Arithmetic & systems', hardware: '4:1 result mux + operators',
    summary: 'Selects arithmetic or logic operations.',
    source: `module alu(input logic [7:0] a, b, input logic [1:0] op, output logic [7:0] result);
always_comb begin
    case(op)
        2'b00: result = a + b;
        2'b01: result = a - b;
        2'b10: result = a & b;
        default: result = a | b;
    endcase
end
endmodule`,
  },
  hierarchy: {
    label: 'Module hierarchy', filename: 'hierarchy.sv', category: 'Arithmetic & systems', hardware: 'Child module instance', topModule: 'hierarchy_top',
    summary: 'A top module composed from a reusable adder block.', takeaway: 'Module instances preserve design hierarchy; inspect the Hierarchy tab to see composition.',
    source: `module add_unit(input logic [7:0] a, b, output logic [7:0] y);
    assign y = a + b;
endmodule

module hierarchy_top(input logic [7:0] a, b, output logic [7:0] result);
    add_unit u_add(.a(a), .b(b), .y(result));
endmodule`,
  },
  uart: {
    label: 'UART transmitter', filename: 'uart_tx.sv', category: 'Arithmetic & systems', hardware: 'Frame register + counter + control',
    summary: 'A compact serial transmitter data path.', takeaway: 'A behavioral clocked process becomes registers, comparisons, and control-enabled paths.',
    source: `module uart_tx(input logic clk, rst_n, start, input logic [7:0] data, output logic tx, busy);
logic [3:0] bit_index; logic [9:0] frame;
always_ff @(posedge clk) begin
    if (!rst_n) begin bit_index <= 0; busy <= 0; tx <= 1; end
    else if (start && !busy) begin frame <= {1'b1,data,1'b0}; busy <= 1; bit_index <= 0; end
    else if (busy) begin tx <= frame[bit_index]; bit_index <= bit_index + 1; if (bit_index == 9) busy <= 0; end
end
endmodule`,
  },
  apb: {
    label: 'APB register', filename: 'apb_regs.sv', category: 'Arithmetic & systems', hardware: 'Bus-qualified register',
    summary: 'A register written only during a valid APB transfer.', takeaway: 'Protocol conditions combine into the enable path of the storage element.',
    source: `module apb_regs(input logic PCLK, PRESETn, PSEL, PENABLE, PWRITE, input logic [7:0] PWDATA, output logic [7:0] PRDATA);
logic [7:0] control;
always_ff @(posedge PCLK) begin
    if (!PRESETn) control <= 8'h00;
    else if (PSEL && PENABLE && PWRITE) control <= PWDATA;
end
assign PRDATA = control;
endmodule`,
  },
  fifo: {
    label: 'Synchronous FIFO', filename: 'fifo.sv', category: 'Arithmetic & systems', hardware: 'Memory + pointer register',
    summary: 'Small storage structure with write control.',
    source: `module fifo(input logic clk, rst_n, wr_en, input logic [7:0] data_in, output logic [7:0] data_out);
logic [7:0] mem [0:3]; logic [1:0] ptr;
always_ff @(posedge clk) begin
    if (!rst_n) ptr <= '0;
    else if (wr_en) begin mem[ptr] <= data_in; ptr <= ptr + 1'b1; end
end
assign data_out = mem[ptr];
endmodule`,
  },
};

export const exampleCategories: ExampleCategory[] = ['Logic gates', 'Selection logic', 'Sequential', 'Arithmetic & systems'];
